
import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { fetchSiteInfo } from '../services/dataService';

// --- CIPHER TEXT REVEAL ---
const CipherReveal: React.FC<{ 
    text: string; 
    className?: string; 
    trigger: number; 
    delay?: number;
}> = ({ text, className, trigger, delay = 0 }) => {
    const [display, setDisplay] = useState('');
    const chars = "0123456789!@#$%^&*()_+-=[]{}|;:,.<>?/"; 

    useEffect(() => {
        let iteration = 0;
        let interval: any = null;

        const startTimeout = setTimeout(() => {
            interval = setInterval(() => {
                setDisplay(prev => 
                    text.split("").map((letter, index) => {
                        if (index < iteration) {
                            return text[index];
                        }
                        return chars[Math.floor(Math.random() * chars.length)];
                    }).join("")
                );

                if (iteration >= text.length) {
                    clearInterval(interval);
                }

                iteration += 1 / 3; 
            }, 30); 
        }, delay);

        return () => {
            clearTimeout(startTimeout);
            if (interval) clearInterval(interval);
        };
    }, [text, trigger, delay]);

    return <span className={className}>{display}</span>;
};

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [siteLogo, setSiteLogo] = useState<string | null>(null);
  const [logoAnimTrigger, setLogoAnimTrigger] = useState(0); 
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    fetchSiteInfo().then(info => {
        if (info.logo_url) {
            setSiteLogo(info.logo_url);
        }
    });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    // Forces the Intro to replay even if we are already on Home
    window.dispatchEvent(new Event('play-intro'));
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Manifesto', path: '/manifesto' },
    { name: 'Atlante', path: '/atlante' },
    { name: 'Articoli', path: '/articoli' },
    { name: 'Contatti', path: '/contatti' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.includes(path)) return true;
    return false;
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/80 border-b border-gray-100 py-3 backdrop-blur-md shadow-sm' : 'bg-transparent py-8'}`}>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          
          {/* Logo - CLICKS TRIGGER INTRO */}
          <div className="flex-shrink-0 z-50">
            <Link 
                to="/" 
                onClick={handleLogoClick} 
                onMouseEnter={() => setLogoAnimTrigger(prev => prev + 1)}
                className="group block transition-opacity hover:opacity-70 cursor-pointer"
            >
              {siteLogo ? (
                  <img 
                    src={siteLogo} 
                    alt="Cyberumanità Logo" 
                    className="h-10 md:h-12 w-auto object-contain transition-all duration-500" 
                  />
              ) : (
                  <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-black text-3xl animate-pulse">{'>_'}</span>
                      <div className="flex flex-col justify-center">
                          <div className="leading-none">
                            <CipherReveal 
                                text="CYBER" 
                                trigger={logoAnimTrigger}
                                className="font-sans font-extrabold text-xl tracking-tighter text-black block" 
                            />
                          </div>
                          <div className="leading-none -mt-1">
                            <CipherReveal 
                                text="UMANITÀ" 
                                trigger={logoAnimTrigger}
                                delay={200}
                                className="font-sans font-medium italic text-xs tracking-widest text-black block" 
                            />
                          </div>
                      </div>
                  </div>
              )}
            </Link>
          </div>
          
          {/* Desktop Menu - HOME LINK SKIPS INTRO */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-12">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  // IMPORTANT: Passing 'skipIntro' state tells Home.tsx NOT to show the intro
                  state={item.path === '/' ? { skipIntro: true } : undefined} 
                  className={`text-[11px] font-bold tracking-[0.15em] uppercase transition-all duration-300 relative group ${
                    isActive(item.path) ? 'text-black' : 'text-gray-400 hover:text-black'
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-2 left-0 w-full h-[1px] bg-black transform transition-transform duration-300 origin-left ${isActive(item.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="flex md:hidden z-50">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-black focus:outline-none p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      <div className={`fixed inset-0 bg-white z-40 flex flex-col justify-center items-center transition-all duration-500 transform ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
          <div className="space-y-8 text-center">
            {navItems.map((item, idx) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                state={item.path === '/' ? { skipIntro: true } : undefined}
                className={`block text-4xl font-serif font-bold text-black hover:text-gray-500 transition-colors transform translate-y-0 ${isOpen ? 'animate-slide-up' : ''}`}
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                {item.name}
              </Link>
            ))}
          </div>
      </div>
    </nav>
  );
};

export default Navbar;
